import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet } from 'react-router-dom';
import { Tabs } from '@heroui/react';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import type { LinkedTab } from '../../tabHref';
import type {
    ConfigurationsTab,
    ConfigurationsTabId,
} from './configurationsTabsMeta';

type ConfigurationsPageViewProps = {
    tabs: LinkedTab<ConfigurationsTab>[];
    selectedTabId: ConfigurationsTabId;
};

/**
 * Configurations shell — render only. Same tab bar as Books, without the date
 * range: neither tab reports over a window.
 *
 * Each tab's content is the routed `<Outlet />`. It appears in every panel, but
 * only the selected panel mounts, so exactly one child route renders — the tab
 * bar and the URL can't disagree.
 */
export const ConfigurationsPageView: React.FC<ConfigurationsPageViewProps> = ({
    tabs,
    selectedTabId,
}) => {
    const { t } = useTranslation('pages');

    return (
        <section
            aria-label={t('accounting.configurations.title')}
            className="flex w-full flex-col gap-5"
            {...getNodeTestIdProp('configurations-section')}
        >
            {/* No `onSelectionChange`: each tab is a real link now, and
                React Aria does not report the clicked key through it for link
                tabs — it fires with the key already selected, or not at all.
                Wiring it to a navigate would send the user to the tab they
                were already on. The URL is the single source of truth and
                `selectedKey` reads it. */}
            <Tabs
                className="w-full"
                selectedKey={selectedTabId}
                variant="secondary"
            >
                <Tabs.ListContainer className="min-w-0 max-w-max">
                    <Tabs.List
                        aria-label={t('accounting.configurations.tabsAria')}
                    >
                        {tabs.map((tab) => (
                            // `.tabs__tab` is `w-full` with no nowrap, so on a
                            // narrow screen the labels share the width and wrap
                            // mid-name. Sized to their text instead, they
                            // overflow — which is what the list container's
                            // scroller is for.
                            <Tabs.Tab
                                className="whitespace-nowrap"
                                href={tab.href}
                                id={tab.id}
                                key={tab.id}
                                {...getNodeTestIdProp(
                                    `configurations-tab-${tab.id}`,
                                )}
                            >
                                {t(tab.labelKey)}
                                <Tabs.Indicator />
                            </Tabs.Tab>
                        ))}
                    </Tabs.List>
                </Tabs.ListContainer>
                {tabs.map((tab) => (
                    <Tabs.Panel
                        className="pt-4"
                        id={tab.id}
                        key={tab.id}
                        {...getNodeTestIdProp(`configurations-panel-${tab.id}`)}
                    >
                        <Outlet />
                    </Tabs.Panel>
                ))}
            </Tabs>
        </section>
    );
};
